import { useState, useEffect, useRef, useCallback } from 'react';

/**
 * A hook for debouncing a fast-changing value.
 * 
 * @param value The value to debounce
 * @param delay The delay in milliseconds (default: 500ms)
 * @returns The debounced value
 */
export function useDebounce<T>(value: T, delay: number = 500): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);
  const previousValueRef = useRef<T>(value);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const pendingValueRef = useRef<T>(value);
  const lastDelayRef = useRef<number>(delay);
  
  // Memoize the timeout handler to prevent recreating functions
  const updateDebouncedValue = useCallback(() => {
    setDebouncedValue(pendingValueRef.current);
    timeoutRef.current = null;
  }, []);
  
  useEffect(() => {
    // Only set a new timeout if the value has actually changed or delay changed
    const valueChanged = value !== previousValueRef.current;
    const delayChanged = delay !== lastDelayRef.current;
    
    if (valueChanged || delayChanged) {
      previousValueRef.current = value;
      pendingValueRef.current = value;
      lastDelayRef.current = delay;
      
      // Clear any existing timeout
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      
      // Only create timeout if delay is positive and value actually changed
      if (delay > 0 && valueChanged) {
        // Use requestIdleCallback when available for better performance
        if (typeof window !== 'undefined' && 'requestIdleCallback' in window) {
          timeoutRef.current = setTimeout(() => {
            window.requestIdleCallback(updateDebouncedValue, { timeout: delay });
          }, delay) as NodeJS.Timeout;
        } else {
          timeoutRef.current = setTimeout(updateDebouncedValue, delay);
        }
      } else if (delay <= 0 && valueChanged) {
        // If no delay, update immediately but only if value changed
        setDebouncedValue(value);
      }
    }
    
    // Cleanup function to clear timeout when component unmounts
    // or when value/delay changes
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, [value, delay, updateDebouncedValue]);
  
  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, []);
  
  return debouncedValue;
} 